// Drivers admin API fetcher — Wave E.

import { apiClient } from './client';
import { endpoints } from './endpoints';
import type {
  CreateDriverInput,
  DriverListResponse,
  DriverSummary,
  UpdateDriverInput,
} from '../types/api';

export interface ListDriversFilters {
  page: number;
  limit?: number;
  status?: 'active' | 'deactivated' | 'all';
  q?: string;
}

export async function listDrivers(filters: ListDriversFilters): Promise<DriverListResponse> {
  const params = new URLSearchParams();
  params.set('page', String(filters.page));
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.status) params.set('status', filters.status);
  if (filters.q) params.set('q', filters.q);
  const res = await apiClient.get<DriverListResponse>(
    `${endpoints.drivers.list}?${params.toString()}`,
  );
  return res.data;
}

export interface DriverDevice {
  driver_uuid: string;
  driver_name: string;
  app_version: string | null;
  platform: 'ios' | 'android' | null;
  os_version: string | null;
  last_seen_at: string | null;
  push_registered: boolean;
  last_location_at: string | null;
  /** true = below the minimum supported version; null = unknown. */
  below_minimum: boolean | null;
}

export interface DriverDevicesResponse {
  minimum_version: string | null;
  latest_version: string | null;
  drivers: DriverDevice[];
}

export async function getDriverDevices(): Promise<DriverDevicesResponse> {
  const res = await apiClient.get<DriverDevicesResponse>(endpoints.drivers.appDevices);
  return res.data;
}

export async function getDriver(uuid: string): Promise<DriverSummary> {
  const res = await apiClient.get<{ driver: DriverSummary }>(
    endpoints.drivers.detail(uuid),
  );
  return res.data.driver;
}

export async function createDriver(input: CreateDriverInput): Promise<DriverSummary> {
  const res = await apiClient.post<
    { driver: DriverSummary; temporary_password: string | null },
    CreateDriverInput
  >(endpoints.drivers.create, input);
  return res.data.driver;
}

export async function updateDriver(
  uuid: string,
  input: UpdateDriverInput,
): Promise<DriverSummary> {
  const res = await apiClient.put<{ driver: DriverSummary }, UpdateDriverInput>(
    endpoints.drivers.update(uuid),
    input,
  );
  return res.data.driver;
}

export async function deleteDriver(uuid: string): Promise<void> {
  await apiClient.delete(endpoints.drivers.delete(uuid));
}

export async function setDriverStatus(
  uuid: string,
  status: 'active' | 'deactivated',
): Promise<DriverSummary> {
  const res = await apiClient.patch<
    { driver: DriverSummary },
    { status: 'active' | 'deactivated' }
  >(endpoints.drivers.setStatus(uuid), { status });
  return res.data.driver;
}
